import { computed, defineComponent, ref, watch, watchEffect } from "vue";
import debounce from "lodash.debounce";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { AnalyticEvents } from "@fresh8/types/src/analytics";

import { useHandleAnalytics } from "../lib/analytics";
import CarouselNavigation from "./CarouselNavigation.vue";
import {
  useTemplateSize,
  SMALL_RESPONSIVE,
  LEADERBOARD,
  LARGE_LEADERBOARD,
  LARGE_RESPONSIVE,
  BILLBOARD,
  SKYSCRAPER,
  LARGE_MPU
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectAdSize } from "../hooks/useAdSize";
import { injectCarouselContext } from "../lib/provideInjectors";

// Hack to get around importing vue files into JSX
const _CarouselNavigation = CarouselNavigation as any;

const useStyles = createUseStyles({
  carousel: {
    display: "flex",
    flex: "1",
    position: "relative",
    alignSelf: "flex-start",
    width: "100%",
    height: "100%"
  },
  slidesContainer: {
    display: "flex",
    flex: "1 0 100%",
    position: "relative",
    boxSizing: 'content-box',
    "& .current": {
      transform: "translateX(0%)"
    },
    "& .previous": {
      transform: "translateX(-100%)"
    },
    "& .next": {
      transform: "translateX(100%)"
    },
    "&:first-child": {
      marginRight: "50px"
    }
  },
  slide: {
    height: "100%",
    flex: 1,
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > *": {
      marginRight: props => props.padding + "px"
    },
    "&:last-of-type": {
      "& > div": {
        marginRight: 0
      }
    },
    "&.moving": {
      transition: "transform 0.3s ease"
    }
  },
  navigation: {
    height: "100%",
    position: "absolute",
    width: "30px",
    flex: "0 0 30px",
    zIndex: 99,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&.small-slider": {
      "& .nav-button": {
        transition: "all 0.1s ease",
        position: "relative",
        left: props => props.isMicro ? "-2px" : "-8px",
        opacity: 0.5,
        "& > svg": {
          transition: "all 0.1s ease",
          position: "relative",
          left: props => props.isMicro ? "-2px" : "-8px"
        }
      },
      "&:hover": {
        "& .nav-button": {
          left: "0px",
          opacity: 1,
          "& > svg": {
            left: "-4px"
          }
        }
      }
    }
  },
  navNext: {
    justifyContent: "flex-end",
    right: "-10px"
  },
  navPrev: {
    marginLeft: "-5px",
    justifyContent: "flex-start",
    left: "-10px"
  }
});

const getBreakpoints = size => {
  switch (size.name) {
    case SMALL_RESPONSIVE.name:
      return {
        adPadding: 6,
        itemMaxWidth: 380,
        offSet: 0,
        previousWidth: 38,
        teaseWidth: 38
      };
    case LEADERBOARD.name:
      return {
        adPadding: 4,
        itemMaxWidth: 428,
        offSet: 143,
        previousWidth: 30,
        teaseWidth: 150
      };
    case LARGE_LEADERBOARD.name:
      return {
        adPadding: 4,
        itemMaxWidth: 528,
        offSet: 143,
        previousWidth: 30,
        teaseWidth: 150
      };
    case LARGE_RESPONSIVE.name:
    case BILLBOARD.name:
      return {
        adPadding: 8,
        itemMaxWidth: 360,
        offSet: 0,
        previousWidth: 60,
        teaseWidth: 60
      };
    case SKYSCRAPER.name:
      return {
        adPadding: 8,
        itemMaxWidth: 230,
        offSet: 0,
        previousWidth: 4,
        teaseWidth: 4,
        navigatorWidth: 16
      };
    case LARGE_MPU.name:
      return {
        adPadding: 8,
        itemMaxWidth: 370,
        offSet: 0,
        previousWidth: 32,
        teaseWidth: 32
      }
    default:
      return {
        adPadding: 8,
        itemMaxWidth: 370,
        offSet: 0,
        previousWidth: 42,
        teaseWidth: 42
      };
  }
};

export default defineComponent({
  name: "Carousel",
  components: { CarouselNavigation },
  setup(props, context) {
    const $handleAnalytics = useHandleAnalytics();
    const activeSlide = ref(0);
    const moving = ref(false);
    const { size } = useTemplateSize();
    const adSize = injectAdSize();
    const carouselContext = injectCarouselContext();

    const slotItems = [];
    // Take each slot and merge their children together to make up the slides
    for (const slot of context?.slots?.default?.() || []) {
      if (slot.children != "v-if") {
        for (const child of slot.children as []) {
          slotItems.push(child);
        }
      }
    }

    const adWidth = computed(() => {
      return (size && size.value.width) || adSize.value.width;
    });

    const breakpointSizings = computed(() => {
      return getBreakpoints(size.value);
    });

    const itemsOnView = computed(() => {
      if (adWidth) {
        const result = Math.round(
          (adWidth.value - breakpointSizings.value.offSet - breakpointSizings.value.teaseWidth) /
            breakpointSizings.value.itemMaxWidth
        );
        return slotItems.length > result ? result : slotItems.length;
      }
      return 1;
    });

    watchEffect(() => {
      carouselContext.value.itemsOnView = itemsOnView.value
    })

    const displayNavigation = computed(
      () => slotItems.length > itemsOnView.value
    );

    const nextSlideTeaseWidth = computed(() => {
      if (!displayNavigation.value) {
        return 0;
      }

      return breakpointSizings.value.teaseWidth;
    });

    const previousSlideWidth = computed(()=>{
      if (!displayNavigation.value) {
        return 0;
      }

      return breakpointSizings.value.previousWidth;
    });

    const prevNavigatorWidth = computed(()=>{
      if (breakpointSizings.value.navigatorWidth) {
        return `${breakpointSizings.value.navigatorWidth}px`
      }
      return activeSlide.value !== lastActiveSlideIndex.value
                  ? `${previousSlideWidth.value}px`
                  : "initial"
    });

    const navigatorWidth = computed(() => {
      if (breakpointSizings.value.navigatorWidth) {
        return `${breakpointSizings.value.navigatorWidth}px`
      }
      return activeSlide.value !== lastActiveSlideIndex.value
                  ? `${nextSlideTeaseWidth.value}px`
                  : "initial"
    });

    const itemWidth = computed(() => {
      return (
        (adWidth.value -
          breakpointSizings.value.offSet -
          (breakpointSizings.value.adPadding * 2) -
          nextSlideTeaseWidth.value) /
        itemsOnView.value
      );
    });

    const lastActiveSlideIndex = computed(
      () => slotItems.length - itemsOnView.value
    );

    const handleInteraction = () => {
      const statsEvent = AnalyticEvents.INTERACTED;
      const statsDesc = "Carousel Navigation";

      $handleAnalytics(statsEvent, statsDesc);
    };

    // prevents jarring transitions when clicking rapidly.
    const stopMoving = debounce(() => (moving.value = false), 500, true);

    const moveBackward = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      moving.value = true;
      if (activeSlide.value > 0) {
        activeSlide.value--;
      }
      stopMoving();
      handleInteraction();
    };

    const moveForward = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      moving.value = true;
      if (activeSlide.value === lastActiveSlideIndex.value) {
        activeSlide.value = 0;
      } else {
        activeSlide.value++;
      }
      stopMoving();
      handleInteraction();
    };

    // Makes sure the current active card persists when changing window size.
    watch(
      lastActiveSlideIndex,
      (value, oldValue) => {
        if (value !== oldValue && activeSlide.value === oldValue) {
          activeSlide.value = value;
        }
      },
      {}
    );

    const getSlideOffset = index => {
      if (adWidth.value && itemWidth.value) {
        let calc = (index - activeSlide.value) * itemWidth.value;

        if (activeSlide.value === lastActiveSlideIndex.value && lastActiveSlideIndex.value !== 0) {
          // Only slides enough to line last card up with the right edge of ad.
          calc =
            adWidth.value -
            breakpointSizings.value.offSet -
            breakpointSizings.value.adPadding * 2 +
            // margin between slides
            -itemWidth.value * (slotItems.length - index);
        }
        return `translateX(${calc}px)`;
      }
    };

    const reativeStyles = computed(() => ({
      padding: breakpointSizings.value?.adPadding || 8,
      isMicro: size.value.name === SKYSCRAPER.name
    }));
    const classes = useStyles(reativeStyles);

    return () => (
      <div class={classes.value.carousel}>
        {activeSlide.value > 0 && (
          <div
            style={{
              width: prevNavigatorWidth.value
            }}
            class={[
              classes.value.navigation,
              classes.value.navPrev,
              (size.value.name === SMALL_RESPONSIVE.name ||
                size.value.name === LEADERBOARD.name ||
                size.value.name === LARGE_LEADERBOARD.name ||
                size.value.name === SKYSCRAPER.name) &&
                "small-slider"
            ]}
            onClick={moveBackward}
          >
            <_CarouselNavigation direction="previous" class="nav-button previous" />
          </div>
        )}

        <div class={classes.value.slidesContainer}>
          {slotItems.map((item, index) => (
            <div
              class={[classes.value.slide, moving.value ? "moving" : null, 'slide']}
              key={index}
              style={{
                transform: getSlideOffset(index),
                width: `${itemWidth.value}px`
              }}
            >
              {item}
            </div>
          ))}
        </div>

        {displayNavigation.value && (
          <div
            style={{
              // sets the width to the width of the teased card if no displaying last card.
              width: navigatorWidth.value
            }}
            class={[classes.value.navigation, classes.value.navNext]}
            onClick={moveForward}
          >
            <_CarouselNavigation
              direction="next"
              return={activeSlide.value === lastActiveSlideIndex.value}
              class="nav-button next"
            />
          </div>
        )}
      </div>
    );
  }
});
